/**
 * @module hooks/useRpdmWave2Gates
 * @summary Feature-flag composition hook for the 8 Wave 2 client flags.
 * @description Derives every gate boolean from `useAuth().isFlagEnabled` —
 *   the existing authoritative configuration source populated on session
 *   bootstrap by the backend `/auth/me` response. Never polls, never
 *   fetches, never opens a timer (§ 11.0-D).
 *
 * ## Purpose
 * Give delivery surfaces a single, memoised object describing which
 * Wave 2 surfaces the current session may render. Client-side gating is
 * a UX convenience; the server remains authoritative (FC-6).
 *
 * ## Inputs / Outputs
 * - No arguments.
 * - Returns:
 *   {
 *     w2Master, hierarchy, reviewer, evaluatorAuth,
 *     dependency, progress, effort, effortAnalytics,
 *     failedFlags: string[]
 *   }
 *
 * ## Invariants (Phase 1a · § 11.0-D)
 * - Reads exclusively via `useAuth().isFlagEnabled(name)`.
 * - No `setInterval` / `setTimeout` / `fetch` / `axios` occurrence in
 *   this file — enforced by a Phase 1c static grep test.
 * - Value recomputation is `useMemo`-bounded; the memo re-runs only when
 *   the underlying `isFlagEnabled` reference (a `useCallback`) changes,
 *   which happens exactly on `featureFlags` state transitions inside
 *   `AuthContext`.
 * - Side-effect free at import time. Merely importing this hook starts
 *   nothing.
 *
 * ## Future consuming phase
 * - Phase 2 → 5: every delivery surface calls this hook first and, if
 *   the relevant gate is false, renders `<FailClosedPlaceholder
 *   flagsFailed={failedFlags}/>` before mounting any real content.
 */

import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * The 8 Wave 2 client feature flags, in composition order.
 * Kept as a module-scope frozen array so no consumer can mutate the list.
 * @type {ReadonlyArray<string>}
 */
export const RPDM_W2_FLAG_NAMES = Object.freeze([
    "rpdm_workspace_enabled",
    "rpdm_w2_master_enabled",
    "rpdm_w2_hierarchy_enabled",
    "rpdm_w2_reviewer_enabled",
    "rpdm_w2_evaluator_authoritative",
    "rpdm_w2_dependency_enabled",
    "rpdm_w2_progress_enabled",
    "rpdm_w2_effort_enabled",
    "rpdm_w2_effort_analytics_enabled",
]);

/**
 * @returns {{
 *   w2Master: boolean,
 *   hierarchy: boolean,
 *   reviewer: boolean,
 *   evaluatorAuth: boolean,
 *   dependency: boolean,
 *   progress: boolean,
 *   effort: boolean,
 *   effortAnalytics: boolean,
 *   failedFlags: ReadonlyArray<string>,
 * }}
 * @invariant Runs no side effect. Never triggers a network request.
 */
export function useRpdmWave2Gates() {
    const { isFlagEnabled } = useAuth();
    return useMemo(() => {
        const workspace = isFlagEnabled("rpdm_workspace_enabled");
        const master = workspace && isFlagEnabled("rpdm_w2_master_enabled");
        const hierarchy = master && isFlagEnabled("rpdm_w2_hierarchy_enabled");
        const reviewer = master && isFlagEnabled("rpdm_w2_reviewer_enabled");
        const evaluatorAuth = master && isFlagEnabled("rpdm_w2_evaluator_authoritative");
        const dependency = master && isFlagEnabled("rpdm_w2_dependency_enabled");
        const progress = master && isFlagEnabled("rpdm_w2_progress_enabled");
        const effort = master && isFlagEnabled("rpdm_w2_effort_enabled");
        const effortAnalytics = effort && isFlagEnabled("rpdm_w2_effort_analytics_enabled");
        const failed = [];
        if (!workspace) failed.push("rpdm_workspace_enabled");
        if (!isFlagEnabled("rpdm_w2_master_enabled")) failed.push("rpdm_w2_master_enabled");
        if (!isFlagEnabled("rpdm_w2_hierarchy_enabled")) failed.push("rpdm_w2_hierarchy_enabled");
        if (!isFlagEnabled("rpdm_w2_reviewer_enabled")) failed.push("rpdm_w2_reviewer_enabled");
        if (!isFlagEnabled("rpdm_w2_evaluator_authoritative")) failed.push("rpdm_w2_evaluator_authoritative");
        if (!isFlagEnabled("rpdm_w2_dependency_enabled")) failed.push("rpdm_w2_dependency_enabled");
        if (!isFlagEnabled("rpdm_w2_progress_enabled")) failed.push("rpdm_w2_progress_enabled");
        if (!isFlagEnabled("rpdm_w2_effort_enabled")) failed.push("rpdm_w2_effort_enabled");
        if (!isFlagEnabled("rpdm_w2_effort_analytics_enabled")) failed.push("rpdm_w2_effort_analytics_enabled");
        return {
            w2Master: master,
            hierarchy,
            reviewer,
            evaluatorAuth,
            dependency,
            progress,
            effort,
            effortAnalytics,
            failedFlags: Object.freeze(failed),
        };
    }, [isFlagEnabled]);
}
